import { BankStatementDoc } from '../../../../../types';
import { getMaturityDate, isBeforeMaturityDate, isOverXDaysAgo } from '../../../../../utilities/validation';
import { Moment } from 'moment';

type BankStatementDocMeta = Pick<
  BankStatementDoc,
  | 'isLogoShown'
  | 'isDateShown'
  | 'isNameAccShown'
  | 'isInEnglish'
  | 'hasProvidedTranslation'
  | 'hasAmountInAccount'
  | 'hasClosingBalanceDate'
  | 'dateOfClosingBalance'
  | 'dateBelowReqAmount'
  | 'dateOfClosingBalance'
>;

const getDateClosingError = (userDate: string, hasAlternateMaturityDate: boolean, maturityDate: Moment) => {
  let errors = [];
  //  over 31 days ago - Highlight condition (4:2)
  if (isOverXDaysAgo(userDate, 31)) {
    errors.push('4.2');
  }
  // is before alternate maturity date (28 days from when balance dropped below amount)
  // - Highlight condition (5.1, 5.2, 5.3)
  if (hasAlternateMaturityDate && isBeforeMaturityDate(userDate, maturityDate)) {
    errors = [...errors, ...['5.1', '5.2', '5.3']];
  }
  return errors;
};

const getHighlightedErrorSections = (doc: BankStatementDocMeta) => {
  const {
    isLogoShown,
    isDateShown,
    isNameAccShown,
    isInEnglish,
    hasProvidedTranslation,
    hasAmountInAccount,
    hasClosingBalanceDate
  } = doc;

  // Are there any errors?
  // highlight the specific sections
  const isLogoShownErrorHighlight = isLogoShown === false ? ['1.1'] : [];
  const isDateShownErrorHighlight = isDateShown === false ? ['1.2'] : [];
  const isNameAccShownErrorHighlight = isNameAccShown === false ? ['2.1'] : [];
  const isInEnglishErrorHighlight =
    isInEnglish === false && hasProvidedTranslation === false ? ['3.1', '3.2', '3.3', '3.4'] : [];
  const hasAmountInAccountErrorHighlight = hasAmountInAccount === false ? ['5.1', '5.2', '5.3'] : [];
  const hasClosingBalanceDateErrorHighlight = hasClosingBalanceDate === false ? ['4.2', '5.2', '5.3'] : [];
  let dateBelowReqAmountErrorHighlight: string[] = [];

  /* We need to determine that the student has a statement that:
  // Has a closing balance no older than 30 days
  // Has had the amount in the account for a period of 28 days

  // If the balance has fallen below the amount stated in the last 60 days
  // Check that the date it has fallen below is not within 28 days
  // of the closing balance.

  // This will show that the student has had the funds in their account for a 28 day period (is mature).
  */
  let maturityDate: Moment = getMaturityDate();
  let hasAlternateMaturityDate = false;

  if (doc.dateBelowReqAmount) {
    maturityDate = getMaturityDate(doc.dateBelowReqAmount);
    hasAlternateMaturityDate = true;

    if (hasClosingBalanceDate) {
      if (isBeforeMaturityDate(doc.dateOfClosingBalance, maturityDate)) {
        dateBelowReqAmountErrorHighlight = ['5.1', '5.2', '5.3'];
      }
    }
  }

  const dateOfClosingBalanceErrorHighlight =
    hasClosingBalanceDate === true
      ? getDateClosingError(doc.dateOfClosingBalance || '', hasAlternateMaturityDate, maturityDate)
      : [];

  if (
    isLogoShownErrorHighlight.length ||
    isDateShownErrorHighlight.length ||
    isNameAccShownErrorHighlight.length ||
    isInEnglishErrorHighlight.length ||
    hasAmountInAccountErrorHighlight.length ||
    dateBelowReqAmountErrorHighlight.length ||
    hasClosingBalanceDateErrorHighlight.length ||
    dateOfClosingBalanceErrorHighlight.length
  ) {
    // unique values only AND
    // remove empty strings in array
    return [
      ...new Set(
        [
          ...isLogoShownErrorHighlight,
          ...isDateShownErrorHighlight,
          ...isNameAccShownErrorHighlight,
          ...isInEnglishErrorHighlight,
          ...hasAmountInAccountErrorHighlight,
          ...dateBelowReqAmountErrorHighlight,
          ...hasClosingBalanceDateErrorHighlight,
          ...dateOfClosingBalanceErrorHighlight
        ].filter(Boolean)
      )
    ];
  }
  return [];
};

export default getHighlightedErrorSections;
